/* global google */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps/dist/index.umd';

const eventMap = {
  onCircleComplete: 'circlecomplete',
  onMarkerComplete: 'markercomplete',
  onOverlayComplete: 'overlaycomplete',
  onPolygonComplete: 'polygoncomplete',
  onPolylineComplete: 'polylinecomplete',
  onRectangleComplete: 'rectanglecomplete',
};

const DrawingManager = ({
  options,
  drawingMode,
  onCircleComplete,
  onMarkerComplete,
  onOverlayComplete,
  onPolygonComplete,
  onPolylineComplete,
  onRectangleComplete,
  onLoad,
  onUnmount,
}) => {
  const map = useMap();
  const [manager, setManager] = useState(null);
  const listenersRef = useRef([]);

  const handlers = useMemo(
    () => ({
      onCircleComplete,
      onMarkerComplete,
      onOverlayComplete,
      onPolygonComplete,
      onPolylineComplete,
      onRectangleComplete,
    }),
    [
      onCircleComplete,
      onMarkerComplete,
      onOverlayComplete,
      onPolygonComplete,
      onPolylineComplete,
      onRectangleComplete,
    ]
  );

  useEffect(() => {
    if (!map || !google?.maps?.drawing) return undefined;

    const drawingManager = new google.maps.drawing.DrawingManager({
      ...(options || {}),
      map,
    });

    if (drawingMode !== undefined) {
      drawingManager.setDrawingMode(drawingMode);
    }

    setManager(drawingManager);
    if (onLoad) onLoad(drawingManager);

    return () => {
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [];
      drawingManager.setMap(null);
      if (onUnmount) onUnmount(drawingManager);
      setManager(null);
    };
  }, [map, options, drawingMode, onLoad, onUnmount]);

  useEffect(() => {
    if (!manager || drawingMode === undefined) return;
    manager.setDrawingMode(drawingMode);
  }, [manager, drawingMode]);

  useEffect(() => {
    if (!manager || !options) return;
    manager.setOptions(options);
  }, [manager, options]);

  useEffect(() => {
    if (!manager) return;
    listenersRef.current.forEach((l) => l.remove());
    listenersRef.current = [];

    Object.entries(eventMap).forEach(([propName, eventName]) => {
      const handler = handlers[propName];
      if (handler) {
        listenersRef.current.push(
          manager.addListener(eventName, (...args) => handler(...args))
        );
      }
    });
  }, [manager, handlers]);

  return null;
};

export default DrawingManager;

